import { InjectionToken } from '@angular/core';
import type {
  AudioProcessingConstraints,
  ConferenceOptions,
  JitsiInitOptions,
} from './jitsi-config';

/**
 * The Jitsi surface this application uses, expressed in our own terms.
 *
 * Every consumer depends on this interface rather than on `JitsiMeetJS`, for two
 * reasons. It keeps `any` and the library's quirks inside one adapter (D21). And
 * it makes the fake in `fake-jitsi-api.ts` a drop-in, which is what lets the
 * toggle-does-not-rebuild and teardown-leaves-no-residue tests run in a browser
 * with no hardware attached.
 */

export interface LocalTrack {
  getType(): 'audio' | 'video';
  /** The underlying WebRTC track — where `getSettings()` lives. */
  getTrack(): MediaStreamTrack;
  getDeviceId(): string;
  isMuted(): boolean;
  mute(): Promise<void>;
  unmute(): Promise<void>;
  dispose(): Promise<void>;
  attach(element: HTMLMediaElement): void;
  detach(element: HTMLMediaElement): void;
}

export interface RemoteTrack {
  getType(): 'audio' | 'video';
  getParticipantId(): string;
  isMuted(): boolean;
  attach(element: HTMLMediaElement): void;
  detach(element: HTMLMediaElement): void;
}

export interface CreateMicTrackOptions {
  /**
   * Required, never optional. The whole of D11 is that the microphone is pinned
   * to an explicitly resolved built-in device rather than left to policy.
   */
  micDeviceId: string;
  constraints: AudioProcessingConstraints;
}

export interface CreateCameraTrackOptions {
  cameraDeviceId?: string;
  resolution?: string;
}

/**
 * Conference events, named rather than passed as raw strings.
 *
 * Every subscription returns its own unsubscribe function, so teardown does not
 * depend on remembering to pass the identical handler reference back to `off` —
 * a leaked listener is residue the §10A.3 assertion looks for.
 */
export interface ConferenceEvents {
  onRemoteTrackAdded(handler: (track: RemoteTrack) => void): () => void;
  onRemoteTrackRemoved(handler: (track: RemoteTrack) => void): () => void;
  onUserJoined(handler: (id: string, displayName: string) => void): () => void;
  onUserLeft(handler: (id: string) => void): () => void;
  onTrackMuteChanged(handler: (track: RemoteTrack) => void): () => void;
  onConferenceFailed(handler: (error: string) => void): () => void;

  /**
   * Who the bridge currently considers the dominant speaker.
   *
   * The id is null when nobody is speaking. Purely presentational — it drives
   * the speaking indicator on a tile and must never gate audio, which the
   * bridge already routes on its own.
   */
  onDominantSpeakerChanged(
    handler: (id: string | null) => void,
  ): () => void;

  /**
   * The media path dropped, but the conference object is still alive.
   *
   * This is emphatically *not* a rebuild signal. lib-jitsi-meet recovers the
   * transport by itself and the local tracks are untouched throughout — so
   * reacting by re-creating anything would take a self-healing blip and turn it
   * into the §3 defect. The only correct response is to tell the user and wait.
   */
  onConnectionInterrupted(handler: () => void): () => void;
  onConnectionRestored(handler: () => void): () => void;
  /**
   * The bridge channel to the videobridge opened (true) or closed (false).
   *
   * Through the bridge the far side's video is only sent once this side's
   * receiver constraints reach the videobridge over this channel, so a call
   * whose channel never opens has audio but no video in either direction.
   */
  onBridgeChannel(handler: (open: boolean) => void): () => void;
  onEndpointMessage(
    handler: (participantId: string, payload: object) => void,
  ): () => void;

  /**
   * The local participant is now in the room — CONFERENCE_JOINED.
   *
   * By the time this fires the server has already delivered everyone who was
   * present before us (XEP-0045 sends existing occupants before our own
   * presence), which is what makes it the moment to check for a duplicate.
   */
  onConferenceJoined(handler: () => void): () => void;

  /** We were removed from the room by someone else. The library leaves itself. */
  onKicked(handler: (reason: string) => void): () => void;

  /** A remote participant's display name arrived or changed. */
  onDisplayNameChanged(
    handler: (id: string, displayName: string) => void,
  ): () => void;

  /**
   * A remote participant's presence property changed.
   *
   * Properties ride in XMPP presence, so they are ordered with join and leave
   * on the same connection — unlike the datachannel, which can drop the last
   * message sent before a hang-up.
   */
  onParticipantPropertyChanged(
    handler: (id: string, name: string, value: string | null) => void,
  ): () => void;

  /**
   * This side's connection statistics, roughly every 10 s while media flows.
   *
   * lib-jitsi-meet's connection-quality module computes them from WebRTC
   * `getStats()`; an older build without it simply never calls the handler.
   */
  onLocalStats(handler: (stats: ConnectionStats) => void): () => void;

  /**
   * The statistics a remote participant broadcast about *their* connection.
   * Fewer fields than local stats — no transport, no per-kind bitrate.
   */
  onRemoteStats(
    handler: (id: string, stats: ConnectionStats) => void,
  ): () => void;
}

/**
 * A connection snapshot, normalised. Every field may be null: the library
 * fills them in as measurements become available, and remote stats carry
 * only a subset.
 */
export interface ConnectionStats {
  /** lib-jitsi-meet's 0–100 estimate. 100 is perfect; under ~30 is unusable. */
  quality: number | null;
  /** Round trip to the bridge. */
  rttMs: number | null;
  packetLossPct: { download: number | null; upload: number | null };
  bitrateKbps: {
    download: number | null;
    upload: number | null;
    audioDownload: number | null;
    audioUpload: number | null;
    videoDownload: number | null;
    videoUpload: number | null;
  };
  /** How media travels. `localCandidate: 'relay'` means through a TURN server. */
  transport: {
    protocol: string;
    localCandidate: string;
    remoteCandidate: string;
    networkType: string;
    p2p: boolean;
  } | null;
}

export interface Conference extends ConferenceEvents {
  join(): void;
  leave(): Promise<void>;
  addTrack(track: LocalTrack): Promise<void>;
  /**
   * D10 — the toggle. Swaps what is sent without opening a capture device, so
   * the microphone track's binding is untouched.
   */
  replaceTrack(
    oldTrack: LocalTrack | null,
    newTrack: LocalTrack | null,
  ): Promise<void>;
  broadcastEndpointMessage(payload: object): void;
  sendTextMessage(text: string): void;
  setDisplayName(name: string): void;

  /**
   * Publish a property in our presence. Set before `join()` and it is carried
   * in the join presence itself, so the far side never sees us without it.
   */
  setLocalParticipantProperty(name: string, value: string): void;

  /**
   * Ask the videobridge for every remote video, up to `maxHeight`.
   *
   * Through the bridge a source is forwarded, and its sender told to encode,
   * only when a receiver asks for it. lib-jitsi-meet asks for nothing on its
   * own — Jitsi Meet's UI does it per tile — so without this a call through
   * the bridge has audio and no video in either direction. Peer-to-peer calls
   * do not need it, which is why desktop-to-desktop calls never showed it.
   * Kept by the library and sent once the bridge channel opens.
   */
  requestRemoteVideo(maxHeight: number): void;

  /** A remote participant's property, or null when unknown or they have left. */
  getParticipantProperty(id: string, name: string): string | null;

  /** Ids of the remote participants currently in the room. */
  getRemoteParticipantIds(): string[];

  on(event: string, handler: (...args: never[]) => void): void;
  off(event: string, handler: (...args: never[]) => void): void;
}

export interface Connection {
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  initConference(roomName: string, options: ConferenceOptions): Conference;
  /**
   * The signalling connection dropped after being established.
   *
   * Unlike an interrupted media path this does not heal itself — the session is
   * gone and has to be rebuilt.
   */
  onDisconnected(handler: (reason: string) => void): () => void;
}

export interface ConnectionOptions {
  domain: string;
  serviceUrl: string;
  roomName: string;
}

export interface JitsiApi {
  init(options: JitsiInitOptions): Promise<void>;
  createConnection(options: ConnectionOptions): Connection;

  /**
   * Create the call's microphone track. This opens a capture device, so §3 says
   * it happens exactly once per session and never again.
   */
  createMicTrack(options: CreateMicTrackOptions): Promise<LocalTrack>;

  /**
   * Create the camera track.
   *
   * Separate from the microphone on purpose. §3's create-once rule is about the
   * *audio* capture device, which is the one the stethoscope contends for; the
   * camera has no such competitor. Creating them in one call would couple the
   * two lifetimes and mean a camera problem could force an audio rebuild.
   */
  createCameraTrack(options?: CreateCameraTrackOptions): Promise<LocalTrack>;

  /**
   * Wrap an existing `MediaStream` as a local track.
   *
   * This is how the stethoscope reaches the conference. It does not call
   * `getUserMedia` and does not open a capture device — verified in the shipped
   * bundle, where the implementation forwards straight to `RTC.createLocalTracks`
   * — so it cannot contend with the microphone's binding (§3.3).
   */
  createTrackFromStream(stream: MediaStream, sourceType: string): LocalTrack;

  enumerateDevices(): Promise<MediaDeviceInfo[]>;

  readonly events: {
    conference: Record<string, string>;
    connection: Record<string, string>;
  };
}

export const JITSI_API = new InjectionToken<JitsiApi>('JITSI_API');
